// Per-axis one-pole / Ornstein-Uhlenbeck smoother with exact exponential alpha.
// Pure: all randomness via injected Rng.

import type { Rng } from '@shared/rng';
import { makeGaussian } from '@shared/rng';

export function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

export function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function smoothstep(e: number): number {
  const x = clamp01(e);
  return x * x * (3 - 2 * x);
}

/**
 * One-pole low-pass toward a target, with optional OU noise.
 * alpha = 1 - exp(-dt/tau) is exact for variable dt and time-scale changes.
 */
export class OUSmoother {
  value: number;
  readonly tauSec: number;
  readonly sigma: number;
  private readonly gaussian = makeGaussian();

  constructor(initial: number, tauSec: number, sigma = 0) {
    this.value = initial;
    this.tauSec = tauSec;
    this.sigma = sigma;
  }

  /** Advance toward target by dt seconds; returns (and stores) the new value. */
  step(target: number, dt: number, rng?: Rng): number {
    if (dt <= 0) return this.value;
    const alpha = 1 - Math.exp(-dt / this.tauSec);
    this.value += alpha * (target - this.value);
    if (this.sigma > 0 && rng) {
      this.value += this.sigma * Math.sqrt(dt) * this.gaussian(rng);
    }
    this.value = clamp01(this.value);
    return this.value;
  }
}
